import { FC } from 'react';
import Modal from '../modal/Modal';
import SortedTable from '../sorted-table/SortedTable';
import { SpecimenCustomDataValue } from '../../types/interfaces/specimenCustomDataKeyModel.interfaces';
import { Specimen } from '../../types/interfaces';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import { dateFormatMMDDYYYY } from '../../utils/commonUtils';
import './quickView.scss';

type QuickViewProps = {
  specimen: Specimen;
  lotDateNumber?: string;
  width?: number;
  height?: number;
  notes?: string;
  customData?: SpecimenCustomDataValue[];
  isOpen: boolean;
  setIsOpen: (value: React.SetStateAction<boolean>) => void;
  onClose: () => void;
};

const embryoStrawHeaders = [
  { displayName: LABEL_CONSTANTS.STRAW_LOT },
  { displayName: LABEL_CONSTANTS.EMBRYO_STAGE },
  { displayName: LABEL_CONSTANTS.EMBRYO_GRADE },
  { displayName: LABEL_CONSTANTS.QUANTITY },
];

const QuickView: FC<QuickViewProps> = ({
  specimen,
  lotDateNumber = `${dateFormatMMDDYYYY(specimen.freezeDate) ?? 'Date Error'} - ${specimen.specimenId!}`,
  notes = specimen.notes,
  customData = specimen.specimenCustomDataValues,
  isOpen = false,
  onClose,
  setIsOpen,
  width = 446,
}) => {
  const embryoStrawTableData =
    specimen?.embryoStraws?.map(straw => [
      {
        content: straw.embryoStrawCode ?? '-',
      },
      {
        content: straw.embryoStage !== null && straw.embryoStage !== '' ? straw.embryoStage : '-',
      },
      {
        content: straw.embryoGrade != null && straw.embryoGrade !== '' ? straw.embryoGrade : '-',
      },
      {
        content: straw.embryoCountPerStraw ?? '-',
      },
    ]) ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={width} ignoreBackdrop>
      <div className="quick-view-container">
        <div className="header">
          <h2 id="qv-title">{PAGE_HEADER_CONSTANTS.QUICK_VIEW}</h2>
          <hr />
        </div>

        <div className="row">
          <div className="l-column">{LABEL_CONSTANTS.LOT_DATE_NUMBER}: </div>
          <div className="r-column">{lotDateNumber ?? '--'}</div>
        </div>

        <div className="row">
          <div className="l-column">{LABEL_CONSTANTS.USER_LOT_NUMBER}: </div>
          <div className="r-column">{specimen?.lotNumber ?? '--'}</div>
        </div>

        {Boolean(customData.length) &&
          customData.map((data: SpecimenCustomDataValue) => {
            if (data.specimenCustomDataKey) {
              return (
                <div className="row" key={data.specimenCustomDataKeyId + '-' + data.specimenId}>
                  <div className="l-column" key={data.specimenCustomDataKey.keyName + '-' + data.specimenId}>
                    {data.specimenCustomDataKey.keyName}:{' '}
                  </div>
                  <div className="r-column" key={data.value + '-' + data.specimenId}>
                    {data.value || '--'}
                  </div>
                </div>
              );
            }
          })}

        {specimen?.embryoStraws?.length ? (
          <div className="row">
            <div className="straw-details-section">
              <h4 className="straw-details-heading">{LABEL_CONSTANTS.STRAW_DETAILS}:</h4>
              <SortedTable
                headers={embryoStrawHeaders}
                data={embryoStrawTableData}
                noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
                height={120}
              />
            </div>
          </div>
        ) : null}

        <div className="notes">
          <p>{LABEL_CONSTANTS.NOTES}:</p>
          <p>{notes}</p>
        </div>
        <button className="button green" id="close-qv-button" onClick={() => setIsOpen(false)}>
          {BUTTON_CONSTANTS.CLOSE}
        </button>
      </div>
    </Modal>
  );
};

export default QuickView;
